import React, { useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Colors } from "../../../configs";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { getPriceListAction } from "../../../redux/actions/pricingAction";
import { IItem } from "../../../typings/interfaces/responses/price-list-response";

const columns = ["Item", "Basic Price ($)"];

interface IPriceListTableProps {
  service: number;
}

export const PriceListTable: React.FC<IPriceListTableProps> = ({ service }) => {
  const dispatch = useAppDispatch();

  const { priceListData, priceListLoading, priceListError } = useAppSelector(
    (state) => state.pricing
  );

  useEffect(() => {
    if (service) {
      dispatch(getPriceListAction(service.toString()));
    }
  }, [service]);

  return (
    <Box
      sx={{
        marginTop: "1rem",
        maxHeight: "38.6rem",
        overflowY: "auto",
        overflowX: "hidden",
        "&::-webkit-scrollbar": { width: "0.5rem" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#ADADAD",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "transparent",
        },
      }}
    >
      {priceListLoading ? (
        <CircularProgress sx={{ display: "block", mx: "auto", my: "2rem" }} />
      ) : priceListError ? (
        <Typography color="error" textAlign="center" mt={2}>
          {priceListError}
        </Typography>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            marginRight: "-6rem",
            paddingLeft: priceListData == null ? "0.1rem" : "0.6rem",
            paddingRight:
              priceListData?.items && priceListData.items.length < 7
                ? "0.6rem"
                : "",
            backgroundColor: Colors.BACKSHADE_GRAY,
            boxShadow: "none",
            border: "none",
          }}
        >
          <Table
            sx={{ borderCollapse: "separate", borderSpacing: "0.4rem 0.4rem" }}
          >
            <TableHead>
              <TableRow>
                {columns.map((col, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      backgroundColor: Colors.ACCENT_GRAY_DISABLED,
                      fontWeight: "bold",
                      width: index === 0 ? "60%" : "40%",
                      ...(index === 0 && { borderTopLeftRadius: "0.71rem" }),
                      ...(index === columns.length - 1 && {
                        borderTopRightRadius: "0.71rem",
                      }),
                    }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {priceListData?.items && priceListData.items.length > 0 ? (
                priceListData.items.map((item) => (
                  <TableRow
                    key={item.subCategoryId}
                    sx={{
                      "& > *": {
                        padding: "1rem",
                        borderRight: "0.3rem solid transparent",
                      },
                      "&:last-child > :first-of-type": {
                        borderBottomLeftRadius: "0.71rem",
                      },
                      "&:last-child > :last-of-type": {
                        borderBottomRightRadius: "0.71rem",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        backgroundColor: Colors.ACCENT_GRAY,
                        color: Colors.MAIN_GRAY,
                      }}
                    >
                      {item.subCategoryName}
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: Colors.ACCENT_GRAY,
                        color: Colors.MAIN_GRAY,
                      }}
                    >
                      ${item.minPrice.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={2}
                    sx={{
                      textAlign: "center",
                      padding: "2rem",
                      backgroundColor: Colors.ACCENT_GRAY,
                      color: Colors.MAIN_GRAY,
                      fontStyle: "italic",
                    }}
                  >
                    No pricing data available.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
